import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronDown, Languages, LogOut, Menu as MenuIcon, Moon, Sun, User as UserIcon, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { admin } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/card'
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@/components/ui/overlay'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { visibleNav } from '@/lib/nav'
import { ROLE_LABEL } from '@/lib/permissions'
import { useLive } from '@/lib/realtime'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid size-8 place-items-center rounded-lg bg-ink shadow-sm">
        <svg viewBox="0 0 24 24" className="size-[18px] fill-brand drop-shadow-[0_0_4px_rgba(255,180,0,0.5)]" aria-hidden="true">
          <path d="M13.6 2 5 13.4h6.1L9.8 22l9.2-12.6h-6.3z" />
        </svg>
      </span>
      <span className="text-[15px] font-semibold tracking-tight">
        Saadhan <span className="text-brand-ink dark:text-brand">Charge</span>
      </span>
    </div>
  )
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth()
  const { t } = useI18n()
  const groups = visibleNav(user)
  return (
    <nav aria-label={t('Main')} className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center px-4">
        <Logo />
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 pb-6 pt-2">
        {groups.map((g) => (
          <div key={g.label}>
            <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">{t(g.label)}</p>
            <ul className="space-y-0.5">
              {g.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/' || item.to === '/admin'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-medium text-text-2 transition-colors hover:bg-surface-3 hover:text-text',
                        isActive && 'bg-brand-soft text-text hover:bg-brand-soft',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute -left-3 top-1.5 h-6 w-1 rounded-r bg-brand" />}
                        <item.icon className={cn('size-4 shrink-0', isActive ? 'text-text' : 'text-muted group-hover:text-text-2')} />
                        <span className="truncate">{t(item.label)}</span>
                        {item.pending && <span className="ml-auto size-1.5 shrink-0 rounded-full bg-brand" title={t('Waiting for backend')} />}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  )
}

function LiveDot() {
  const { state } = useLive()
  const { t } = useI18n()
  const label = state === 'live' ? t('Live') : state === 'connecting' ? t('Connecting…') : t('Offline')
  return (
    <span className="hidden items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-text-2 sm:inline-flex" title={t('Live updates from chargers')}>
      <span className={cn('size-2 rounded-full', state === 'live' ? 'pulse-dot bg-success' : state === 'connecting' ? 'bg-warning' : 'bg-muted')} />
      {label}
    </span>
  )
}

function OperatorSwitcher() {
  const { user, scope, setScope } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const { data } = useQuery({ queryKey: ['admin', 'cpos', 'all'], queryFn: () => admin.cpos({ per_page: 100 }), enabled: user?.role === 'superadmin', staleTime: 300_000 })
  if (user?.role !== 'superadmin') return null
  const current = data?.cpos.find((c) => c.id === scope)
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="outline" size="sm" className="max-w-56">
          <Building2 />
          <span className="truncate">{current ? current.name : t('Choose an operator')}</span>
          <ChevronDown className="text-muted" />
        </Button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuLabel>{t('View the dashboard as')}</MenuLabel>
        <div className="max-h-80 overflow-y-auto">
          {data?.cpos.map((c) => (
            <MenuItem key={c.id} onSelect={() => { setScope(c.id); if (pathname.startsWith('/admin')) navigate('/') }}>
              <span className="truncate">{c.name}</span>
              {c.id === scope && <Badge tone="brand" className="ml-auto">{t('Viewing')}</Badge>}
            </MenuItem>
          ))}
        </div>
        {scope && (
          <>
            <MenuSeparator />
            <MenuItem onSelect={() => { setScope(null); navigate('/admin') }}>{t('Stop viewing an operator')}</MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()
  const { t, lang, setLang } = useI18n()
  const { dark, setTheme } = useTheme()
  const navigate = useNavigate()
  if (!user) return null
  const initials = (user.full_name || user.email || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" aria-label={lang === 'en' ? 'नेपालीमा हेर्नुहोस्' : 'View in English'} onClick={() => setLang(lang === 'en' ? 'ne' : 'en')}>
        <Languages />
      </Button>
      <Button variant="ghost" size="icon" aria-label={dark ? t('Light mode') : t('Dark mode')} onClick={() => setTheme(dark ? 'light' : 'dark')}>
        {dark ? <Sun /> : <Moon />}
      </Button>
      <Menu>
        <MenuTrigger asChild>
          <button className="ml-1 grid size-9 place-items-center rounded-full bg-ink text-xs font-semibold text-brand" aria-label={t('Account')}>
            {initials}
          </button>
        </MenuTrigger>
        <MenuContent>
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium">{user.full_name || user.email || user.phone}</p>
            <p className="truncate text-xs text-text-2">{t(ROLE_LABEL[user.role])}</p>
          </div>
          <MenuSeparator />
          <MenuItem onSelect={() => navigate('/profile')}>
            <UserIcon /> {t('My profile')}
          </MenuItem>
          <MenuItem onSelect={() => setLang(lang === 'en' ? 'ne' : 'en')}>
            <Languages /> {lang === 'en' ? 'नेपाली' : 'English'}
          </MenuItem>
          <MenuSeparator />
          <MenuItem danger onSelect={() => logout()}>
            <LogOut /> {t('Sign out')}
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  )
}

export function AppShell() {
  const { pathname } = useLocation()
  // A new page starts at the top, not where the last one was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  const [open, setOpen] = useState(false)
  const { t } = useI18n()
  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 border-r border-border bg-surface lg:block">
        <Sidebar />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 border-r border-border bg-surface shadow-2xl">
            <Button variant="ghost" size="icon" className="absolute right-2 top-2.5" aria-label={t('Close menu')} onClick={() => setOpen(false)}>
              <X />
            </Button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface/85 px-4 backdrop-blur lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t('Open menu')} onClick={() => setOpen(true)}>
            <MenuIcon />
          </Button>
          <OperatorSwitcher />
          <div className="ml-auto flex items-center gap-2">
            <LiveDot />
            <UserMenu />
          </div>
        </header>
        <main id="main" className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
